import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FactCheck, MapOutlined } from '@mui/icons-material';
import InspectionReport from 'components/parking-agent/InspectionReport';
import ReferenceSelector from 'components/parking-agent/ReferenceSelector';
import SiteHealthMemo from 'components/parking-agent/SiteHealthMemo';
import CommonKitPanel from 'components/parking-agent/CommonKitPanel';
import UsbKitPanel from 'components/parking-agent/UsbKitPanel';
import { useAgentRun } from 'components/parking-agent/useAgentRun';
import {
  InspectionReference,
  SiteInspection,
} from 'services/ParkingAgentService';
import { SiteHealthReport } from 'services/SiteHealthService';

interface ParkingAgentSectionProps {
  projectId: number;
}

const ParkingAgentSection: React.FC<ParkingAgentSectionProps> = ({
  projectId,
}) => {
  const { run, history, loading, busy, error, start, select } =
    useAgentRun<SiteInspection>(projectId, 'inspection');
  // 상태 점검은 현장 서버에 SSH 로 붙어 몇 분 걸린다. 문서 점검과 따로 돌려
  // 각자 끝나는 대로 보여 준다.
  const health = useAgentRun<SiteHealthReport>(projectId, 'site_health');
  const [reference, setReference] = useState<InspectionReference>();
  const [referenceReady, setReferenceReady] = useState(true);
  const changeReference = useCallback(
    (value: InspectionReference | undefined, ready: boolean) => {
      setReference(value);
      setReferenceReady(ready);
    },
    [],
  );
  const startInspection = () =>
    start({ kind: 'inspection', ...(reference ? { reference } : {}) });
  const startAll = () => {
    void startInspection();
    void health.start({ kind: 'site_health' });
  };
  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Typography variant="h5" fontWeight={700}>
        주차 에이전트
      </Typography>
      <Typography color="text.secondary">
        저장된 NVR·CCTV·주차면·ROI 연결을 자동으로 점검하고, 도면에서 주차면을
        복원하며, 현장 설치용 USB 번들을 만듭니다.
      </Typography>
      <ReferenceSelector
        key={projectId}
        siteId={projectId}
        disabled={busy || loading}
        onChange={changeReference}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button
          variant="contained"
          startIcon={<FactCheck />}
          disabled={busy || loading || health.busy || !referenceReady}
          onClick={startAll}
          sx={{ minHeight: 44 }}
        >
          {busy || health.busy ? '현장 점검 중…' : '현장 점검 실행'}
        </Button>
        <Button
          href={`/project/${projectId}/map-editor`}
          variant="outlined"
          startIcon={<MapOutlined />}
          sx={{ minHeight: 44 }}
        >
          맵 에디터에서 주차면 복원
        </Button>
      </Stack>
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
        <Stack spacing={2}>
          <Typography variant="h6">점검 결과</Typography>
          {error && (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  disabled={!run && !referenceReady}
                  onClick={() =>
                    run ? void select(run.id) : void startInspection()
                  }
                >
                  다시 시도
                </Button>
              }
            >
              {error}
            </Alert>
          )}
          {(busy || loading) && (
            <Box role="status">
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1 }}>
                {busy
                  ? '서버에서 점검 중입니다. 화면을 나갔다 돌아와도 결과를 확인할 수 있습니다.'
                  : '저장된 결과를 불러오고 있습니다.'}
              </Typography>
            </Box>
          )}
          {history.length > 1 && (
            <TextField
              select
              label="점검 이력"
              size="small"
              value={run?.id || ''}
              disabled={busy || loading}
              onChange={(e) => void select(e.target.value)}
            >
              {history.map((item) => (
                <MenuItem value={item.id} key={item.id}>
                  {new Date(item.created_at).toLocaleString('ko-KR')}
                </MenuItem>
              ))}
            </TextField>
          )}
          {run?.status === 'failed' && (
            <Alert severity="error">
              {run.error || '점검을 완료하지 못했습니다. 다시 실행해주세요.'}
            </Alert>
          )}
          {run?.status === 'completed' && run.result && (
            <InspectionReport
              key={run.id}
              siteId={projectId}
              report={run.result}
            />
          )}
          {!run && !loading && (
            <Typography color="text.secondary">
              현장 점검을 실행하면 파일별 문제 위치와 연결 상태가 표시됩니다.
            </Typography>
          )}
        </Stack>
      </Paper>
      <SiteHealthMemo
        siteId={projectId}
        latest={
          health.run?.status === 'completed' ? health.run.result : undefined
        }
        busy={health.busy}
        error={
          health.run?.status === 'failed'
            ? health.run.error || '현장 상태를 확인하지 못했습니다.'
            : health.error
        }
      />
      <CommonKitPanel />
      <UsbKitPanel projectId={projectId} />
    </Stack>
  );
};

export default ParkingAgentSection;
